import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { CheckCircle2, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// --- Constants ---

const STATUS_CONFIG = {
  ej_paborjad: { label: 'Ej påbörjad', className: 'bg-muted text-muted-foreground' },
  ej_pagorjad: { label: 'Ej påbörjad', className: 'bg-muted text-muted-foreground' }, // backward-compat for existing DB rows
  pagaende: { label: 'Pågående', className: 'bg-primary/20 text-[#333333]' },
  klar: { label: 'Klar', className: 'bg-success/20 text-success' },
};

const TABS = [
  { id: 'mina', label: 'Mina' },
  { id: 'alla', label: 'Alla' },
  { id: 'forsenade', label: 'Försenade' },
  { id: 'denna_vecka', label: 'Denna vecka' },
];

const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
};

const isOverdue = (deadline, status) => {
  if (!deadline || status === 'klar') return false;
  return new Date(deadline) < new Date();
};

const DeadlineLabel = ({ deadline, status }) => {
  if (!deadline) return null;
  const overdue = isOverdue(deadline, status);
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${overdue ? 'text-destructive' : 'text-muted-foreground'}`}>
      {overdue ? <AlertCircle size={12} /> : <Clock size={12} />}
      {formatDate(deadline)}
      {overdue && ' · Försenad'}
    </span>
  );
};

// --- Main Page ---

const FollowUpsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = user?.role !== 'read_only_assigner' && user?.role !== 'read_only_participant';

  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('mina');
  const [updatingId, setUpdatingId] = useState(null);

  const fetchFollowUps = useCallback(async () => {
    try {
      const params = {};
      if (activeTab === 'mina') params.assignee = 'me';
      if (activeTab === 'forsenade') params.overdue = true;
      if (activeTab === 'denna_vecka') params.this_week = true;
      const res = await api.get('/follow-ups', { params });
      setAllItems(res.data);
    } catch {
      toast.error('Kunde inte hämta uppföljningspunkter');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    fetchFollowUps();
  }, [fetchFollowUps]);

  const handleStatusChange = async (itemId, newStatus) => {
    setUpdatingId(itemId);
    try {
      const res = await api.put(`/follow-ups/${itemId}`, { status: newStatus });
      setAllItems(prev => prev.map(i => i.id === itemId ? { ...i, ...res.data } : i));
      toast.success('Status uppdaterad');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte uppdatera status');
    } finally {
      setUpdatingId(null);
    }
  };

  const openCount = allItems.filter(i => i.status !== 'klar').length;
  const overdueCount = allItems.filter(i => isOverdue(i.deadline, i.status)).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-heading font-black tracking-tight">Uppföljningar</h1>
          <p className="text-muted-foreground mt-2">Uppföljningspunkter från mötesanteckningar</p>
        </div>

        {/* KPI bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">Totalt öppna</p>
              <p className="text-3xl font-heading font-black mt-1">{openCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">Försenade</p>
              <p className={`text-3xl font-heading font-black mt-1 ${overdueCount > 0 ? 'text-destructive' : ''}`}>
                {overdueCount}
              </p>
            </CardContent>
          </Card>
          <Card className="hidden sm:block">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">Totalt</p>
              <p className="text-3xl font-heading font-black mt-1">{allItems.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap border-b pb-2">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary text-[#333333]'
                  : 'bg-muted text-muted-foreground hover:bg-primary/20'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Laddar uppföljningspunkter...
            </CardContent>
          </Card>
        ) : allItems.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle2 size={40} className="mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-muted-foreground text-lg">Inga uppföljningspunkter ännu.</p>
              <p className="text-sm text-muted-foreground mt-1">
                Uppföljningspunkter skapas när du lägger till dem i mötesanteckningar.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {allItems.map(item => {
              const overdue = isOverdue(item.deadline, item.status);
              const statusCfg = STATUS_CONFIG[item.status] || { label: item.status, className: 'bg-muted' };
              return (
                <Card
                  key={item.id}
                  className={`transition-shadow hover:shadow-md ${overdue ? 'border-destructive/40' : ''}`}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm leading-snug">{item.beskrivning}</p>

                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        {item.participant_name && (
                          <button
                            onClick={() => navigate(`/participants/${item.participant_id}`)}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <ExternalLink size={11} />
                            {item.participant_name}
                          </button>
                        )}
                        {item.ansvarig && (
                          <span className="text-xs text-muted-foreground">
                            Ansvarig: <span className="font-medium">{item.ansvarig}</span>
                          </span>
                        )}
                        <DeadlineLabel deadline={item.deadline} status={item.status} />
                      </div>

                      {item.note_rubrik && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Från: {item.note_rubrik}
                          {item.note_datum && ` · ${formatDate(item.note_datum)}`}
                        </p>
                      )}
                    </div>

                    <div className="shrink-0">
                      {canWrite ? (
                        <Select
                          value={item.status}
                          onValueChange={(v) => handleStatusChange(item.id, v)}
                          disabled={updatingId === item.id}
                        >
                          <SelectTrigger className={`h-8 text-xs font-bold rounded-full px-3 border-0 ${statusCfg.className}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ej_paborjad">Ej påbörjad</SelectItem>
                            <SelectItem value="pagaende">Pågående</SelectItem>
                            <SelectItem value="klar">Klar</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusCfg.className}`}>
                          {statusCfg.label}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FollowUpsPage;
